import * as XLSX from 'xlsx';

import { readFileArrayBuffer } from '@/lib/daily-reports/readFileArrayBuffer';

import type { ResultsCell, ResultsSheet } from './sheetCells';

/**
 * Raw cell value, not its display format: margins and adders keep their full
 * precision. Error cells stay errors, since reading `#N/A` as blank would turn
 * a failed lookup into "no cost adder" or "nothing to load under" — and so
 * does a formula saved without its calculated value.
 */
function toCell(cell: XLSX.CellObject | undefined): ResultsCell {
  if (!cell) return null;
  if (cell.t === 'e') return { error: cell.w ?? '#ERROR' };
  const { v } = cell;
  if (cell.f !== undefined && v === undefined) {
    return { error: '#UNCALCULATED' };
  }
  if (
    typeof v === 'string' ||
    typeof v === 'number' ||
    typeof v === 'boolean'
  ) {
    return v;
  }
  return null;
}

function sheetRange(sheet: XLSX.WorkSheet): XLSX.Range | null {
  const ref = sheet['!ref'];
  return ref ? XLSX.utils.decode_range(ref) : null;
}

/** The header row, up to its last named column. */
function headerCells(range: XLSX.Range, sheet: XLSX.WorkSheet): string[] {
  const headers: string[] = [];
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cell = toCell(sheet[XLSX.utils.encode_cell({ r: range.s.r, c })]);
    headers.push(typeof cell === 'string' ? cell : '');
  }
  while (headers.length > 0 && headers.at(-1)?.trim() === '') headers.pop();
  return headers;
}

/**
 * Data rows built from the cells that exist, not the sheet's whole used
 * rectangle: one stray cell far down or right must not make a small workbook
 * allocate millions of empty cells. Columns past the last header are ignored.
 */
function dataRows(
  range: XLSX.Range,
  sheet: XLSX.WorkSheet,
  width: number,
): { rows: ResultsCell[][]; rowNumbers: number[] } {
  const byRow = new Map<number, ResultsCell[]>();
  for (const address of Object.keys(sheet)) {
    if (address.startsWith('!')) continue;
    const { r, c } = XLSX.utils.decode_cell(address);
    const column = c - range.s.c;
    if (r <= range.s.r || column < 0 || column >= width) continue;
    const row = byRow.get(r) ?? Array.from({ length: width }, () => null);
    row[column] = toCell(sheet[address]);
    byRow.set(r, row);
  }
  const ordered = [...byRow.keys()].sort((a, b) => a - b);
  return {
    rows: ordered.map((r) => byRow.get(r) ?? []),
    // Excel rows are 1-based.
    rowNumbers: ordered.map((r) => r + 1),
  };
}

/** One sheet of a supplier workbook (by default its first). */
export function resultsSheetFromWorkbook(
  workbook: XLSX.WorkBook,
  sheetName: string | undefined = workbook.SheetNames[0],
): ResultsSheet {
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
  const range = sheet ? sheetRange(sheet) : null;
  if (!sheet || !range) return { headers: [], rows: [] };
  const headers = headerCells(range, sheet);
  return {
    headers,
    ...dataRows(range, sheet, headers.length),
    headerRowNumber: range.s.r + 1,
    date1904: workbook.Workbook?.WBProps?.date1904 === true,
  };
}

/** How far down a sheet its header row is looked for. */
const HEADER_SCAN_ROWS = 20;

/** A dropped supplier workbook: the sheet being converted, and the others. */
export interface ResultsWorkbookRead {
  sheet: ResultsSheet;
  /** The sheet read, or null for a workbook with no sheets. */
  sheetName: string | null;
  /**
   * The sheets carrying a header row, in workbook order: the ones a user can
   * pick (e.g. FINAL.xlsx's Division tabs, not its empty first sheet).
   */
  resultsSheetNames: string[];
}

function hasHeaderRow(workbook: XLSX.WorkBook, name: string): boolean {
  const sheet = workbook.Sheets[name];
  const range = sheet ? sheetRange(sheet) : null;
  return Boolean(
    sheet && range && headerCells(range, sheet).some((h) => h.trim() !== ''),
  );
}

/**
 * Reads only one sheet: skipping the rest saves seconds on a 30MB price book.
 * A workbook of several sheets has their header rows read first, so the first
 * sheet carrying one is converted rather than, say, an empty `Sheet1`.
 */
export function readResultsData(data: ArrayBuffer): ResultsWorkbookRead {
  const first = XLSX.read(data, { type: 'array', sheets: 0 });
  const firstName = first.SheetNames[0] ?? null;
  if (first.SheetNames.length <= 1) {
    return {
      sheet: resultsSheetFromWorkbook(first),
      sheetName: firstName,
      resultsSheetNames:
        firstName && hasHeaderRow(first, firstName) ? [firstName] : [],
    };
  }
  // `sheetRows` counts Excel rows, not used ones: read a few, so a sheet whose
  // header starts below row 1 is still found.
  const headerRows = XLSX.read(data, {
    type: 'array',
    sheetRows: HEADER_SCAN_ROWS,
  });
  const resultsSheetNames = headerRows.SheetNames.filter((name) =>
    hasHeaderRow(headerRows, name),
  );
  const sheetName = resultsSheetNames[0] ?? firstName;
  return {
    sheet:
      sheetName === firstName
        ? resultsSheetFromWorkbook(first)
        : readResultsSheet(data, sheetName),
    sheetName,
    resultsSheetNames,
  };
}

/** One named sheet of the workbook, e.g. the Division tab the user picked. */
export function readResultsSheet(
  data: ArrayBuffer,
  sheetName: string | null,
): ResultsSheet {
  if (sheetName === null) return { headers: [], rows: [] };
  const workbook = XLSX.read(data, { type: 'array', sheets: sheetName });
  return resultsSheetFromWorkbook(workbook, sheetName);
}

/** The first sheet only, e.g. each workbook of the corporate upload. */
export async function readFirstResultsSheet(file: File): Promise<ResultsSheet> {
  const workbook = XLSX.read(await readFileArrayBuffer(file), {
    type: 'array',
    sheets: 0,
  });
  return resultsSheetFromWorkbook(workbook);
}

export async function readResultsWorkbook(
  file: File,
): Promise<ResultsWorkbookRead & { data: ArrayBuffer }> {
  const data = await readFileArrayBuffer(file);
  return { ...readResultsData(data), data };
}
